import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema, LoginFormData } from '../validation';
import { ErrorMessage } from '../components';
import LoginImage from '../assets/LoginImage';
import MessageIcon from '../assets/MessageIcon';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import authAPI from '../api/authAPI';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { translate } from '../i18n';

const LoginPage = () => {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setApiError(null);
    await handleLogin(data);
  };

  const handleLogin = async (data: LoginFormData) => {
    try {
      const response = await authAPI.login({ ...data });
      const user = response?.data?.user;
      const token = response?.data?.token;
      if (user && token) {
        login(user, token);
      }
    } catch (error: any) {
      const apiErrorMessage =
        error?.response?.data?.message ||
        translate('loginScreen.errorMessage.invalidCredentials');
      setApiError(apiErrorMessage);
      alert(apiErrorMessage);
    }
  };

  const handleInputChange =
    (field: 'username' | 'password') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, e.target.value, { shouldValidate: true });
      clearErrors(field); // Clear errors if any
      setApiError(null); // Clear API error
    };

  return (
    <div className="h-screen w-screen overflow-auto md:overflow-hidden grid grid-rows-[40vh_1fr] md:grid-cols-2 md:grid-rows-none">
      {/* Image Section */}
      <div className="relative w-full h-full md:min-h-screen">
        <LoginImage
          className="w-full h-full"
          preserveAspectRatio="xMidYMid slice"
        />
      </div>

      {/* Login Form Section */}
      <div className="w-full h-full md:min-h-screen md:overflow-auto">
        <div className="w-full h-full flex items-center justify-center p-6 md:p-8">
          <div className="w-full max-w-[400px]">
            <form
              className="w-full bg-white p-8"
              onSubmit={handleSubmit(onSubmit)}
            >
              <h1 className="text-left text-2xl md:text-4xl font-bold text-[#212B36] mb-8">
                {translate('loginScreen.signinToSkybiz')}
              </h1>

              {/* Email Field */}
              <div className="space-y-2 mb-5">
                <label className="block text-base text-start font-medium text-[#1C2434]">
                  Email
                  <div className="relative mt-1">
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <MessageIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="E-mail/Username"
                      className={`w-full pl-4 p-3 rounded-md border ${
                        errors.username || apiError
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      } transition-colors`}
                      {...register('username')}
                      onChange={() => {
                        clearErrors('username');
                        setApiError(null);
                      }}
                      onInput={handleInputChange('username')}
                    />
                  </div>
                </label>
                <ErrorMessage message={errors.username?.message || ''} />
              </div>

              {/* Password Field */}
              <div className="space-y-2 mb-5">
                <label className="block text-base text-start font-medium text-[#1C2434]">
                  Password
                  <div className="relative mt-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      className={`w-full pl-4 pr-10 p-3 rounded-md border ${
                        errors.password || apiError
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      } transition-colors`}
                      {...register('password')}
                      onChange={() => {
                        clearErrors('password');
                        setApiError(null);
                      }}
                      onInput={handleInputChange('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible size={20} />
                      ) : (
                        <AiOutlineEye size={20} />
                      )}
                    </button>
                  </div>
                </label>
                <ErrorMessage message={errors.password?.message || ''} />
              </div>

              {/* API Error Message */}
              {apiError && (
                <div className="text-center text-red-500 mb-5">{apiError}</div>
              )}
              <button
                type="submit"
                className="w-full bg-[#004E9B] text-white py-3 rounded-lg hover:bg-[#004E9B] focus:ring-2 focus:ring-[#004E9B] focus:ring-offset-2 transition-colors font-medium"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
